import { api } from './client'

/**
 * F-056 (TASK-0310): client di `ValidationRulesController` (`/api/validation-rules`) — le regole
 * che il motore di completezza valuta su ogni pratica. Lettura: backoffice; scrittura: solo Admin
 * (il server risponde 403 agli altri). Codice, tipo e ambito di una regola non cambiano dopo la
 * creazione.
 */
export type ValidationRuleKind =
  | 'ProdottoCollegato'
  | 'DataDiFirma'
  | 'ClienteCollegato'
  | 'ContattoCollegato'
  | 'RepartoCollegato'
  | 'ImportoAttesoPresente'
  | 'NumeroMinimoRighe'
  | 'PartitaIvaPresente'
  | 'IbanPresente'
  | 'CertificatoValido'
  | 'DocumentoNonScaduto'
  | 'CompatibilitaCampi'
  | 'DocumentoPresente'

export type ValidationRuleScope = 'TuttiIContratti' | 'Prodotto' | 'Reparto'
export type ValidationRuleSeverity = 'Bloccante' | 'Avviso'

export interface ValidationRule {
  id: number
  code: string
  label: string
  kind: ValidationRuleKind
  scope: ValidationRuleScope
  scopeProductId: number | null
  scopeDepartmentId: number | null
  severity: ValidationRuleSeverity
  weight: number
  threshold: number | null
  /** Solo per i tipi documentali: tipo di documento cercato (confronto senza maiuscole/spazi). */
  requiredDocumentType: string | null
  isActive: boolean
}

export interface ValidationRuleCreateBody {
  code: string
  label: string
  kind: ValidationRuleKind
  scope: ValidationRuleScope
  severity: ValidationRuleSeverity
  weight: number
  scopeProductId?: number | null
  scopeDepartmentId?: number | null
  threshold?: number | null
  requiredDocumentType?: string | null
  isActive?: boolean
}

export interface ValidationRuleUpdateBody {
  label: string
  severity: ValidationRuleSeverity
  weight: number
  threshold?: number | null
  requiredDocumentType?: string | null
  isActive?: boolean
}

/** I due tipi che leggono i documenti agganciati alla pratica. */
export const DOCUMENT_RULE_KINDS: ValidationRuleKind[] = ['DocumentoPresente', 'DocumentoNonScaduto']

export const validationRulesApi = {
  list: (activeOnly = false) =>
    api<ValidationRule[]>(`/api/validation-rules${activeOnly ? '?activeOnly=true' : ''}`),
  create: (body: ValidationRuleCreateBody) =>
    api<ValidationRule>('/api/validation-rules', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: ValidationRuleUpdateBody) =>
    api<ValidationRule>(`/api/validation-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setActive: (id: number, isActive: boolean) =>
    api<ValidationRule>(`/api/validation-rules/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    }),
  /** Tipi di documento richiesti dalle regole attive (qualsiasi utente autenticato). */
  documentTypes: () => api<string[]>('/api/validation-rules/document-types'),
}
